import { WhatsAppSupport } from '@/components/app-footer/app-footer';
import { useStrings } from '@/contexts/strings-context';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { LoaderCircle } from 'lucide-react';
import { FormEvent, useEffect, useId, useRef, useState } from 'react';

interface VerificationModalProps {
  open: boolean;
  title: string;
  description: string;
  cancelText?: string;
  confirmText: string;
  hideCancelAction?: boolean;
  onCancelAction?: () => void;
  onConfirmAction?: (value: string) => void;
  defaultEmail?: string;
  defaultPhone?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  eyebrow?: string;
  helperText?: string;
  isSubmitting?: boolean;
  verifyByPhone?: boolean;
  verifyByEmail?: boolean;
  orderNo?: string;
  token?: string;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function VerificationModal({
  open,
  title,
  description,
  cancelText,
  confirmText,
  hideCancelAction = false,
  onCancelAction,
  onConfirmAction,
  defaultEmail = '',
  defaultPhone = '',
  phoneLabel = 'Phone number',
  phonePlaceholder = '07000123456',
  emailLabel = 'Email address',
  emailPlaceholder = 'you@example.com',
  helperText,
  isSubmitting = false,
  verifyByPhone = false,
  verifyByEmail = !verifyByPhone,
}: VerificationModalProps) {
  const isPhone = verifyByPhone && !verifyByEmail;
  const defaultValue = isPhone ? defaultPhone : defaultEmail;
  const strings = useStrings();

  const resolvedHelperText = helperText ?? `${isPhone ? strings.ENTER_PHONE_TIP : strings.ENTER_EMAIL_TIP}`;

  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  const [value, setValue] = useState(defaultValue);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setErrorMessage('');
    }
  }, [defaultValue, isPhone, open]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) return;

    const trimmedValue = value.trim();
    const normalizedValue = isPhone ? trimmedValue.replace(/[\s().-]/g, '') : trimmedValue;

    if (isPhone ? !/^\+?\d{7,15}$/.test(normalizedValue) : !isValidEmail(normalizedValue)) {
      setErrorMessage(isPhone ? 'Enter a valid phone number (ex. 07000123456)' : 'Enter a valid email address.');
      return;
    }

    setErrorMessage('');
    onConfirmAction?.(normalizedValue);
  };

  return (
    <AlertDialogPrimitive.Root open={open}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[0px]" />

        <AlertDialogPrimitive.Content
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus({ preventScroll: true });
          }}
          data-verification-dialog=""
          className="fixed inset-0 z-[51] pointer-events-none outline-none"
        >
          <div className="pointer-events-auto fixed left-1/2 top-1/2 z-[51] w-[calc(100%_-_32px)] max-w-[430px] -translate-x-1/2 -translate-y-1/2 border border-border bg-background px-[24px] py-[26px] text-foreground outline-none sm:px-[30px] sm:py-[30px]">
            <header>
              <AlertDialogPrimitive.Title className="mt-[9px] text-[clamp(27px,calc(25px_+_0.3vw),28px)] font-bold leading-[31px] text-screwfix-blue-shade-1">
                {title}
              </AlertDialogPrimitive.Title>

              <AlertDialogPrimitive.Description className="mt-[8px] max-w-[350px] [font-family:archivo_narrowregular,sans-serif] text-[clamp(16px,calc(14px_+_0.3vw),17px)] font-normal leading-[21px] text-muted-foreground">
                {description}
              </AlertDialogPrimitive.Description>
            </header>

            <form className="mt-[24px]" noValidate onSubmit={handleSubmit}>
              <label
                className="block  text-[16px] font-bold uppercase leading-[19px] tracking-[0.025em] text-screwfix-blue-shade-2"
                htmlFor={inputId}
              >
                {isPhone ? phoneLabel : emailLabel} <span className="text-danger">*</span>
              </label>

              <input
                aria-describedby={errorMessage ? `${errorId} ${helperId}` : helperId}
                aria-invalid={Boolean(errorMessage)}
                autoComplete={isPhone ? 'tel' : 'email'}
                ref={inputRef}
                className="mt-[7px] h-[48px] w-full rounded-[3px] border border-input bg-background px-[13px] [font-family:archivo_narrowregular,sans-serif] text-[clamp(17px,calc(15px_+_0.3vw),18px)] font-normal text-foreground shadow-[inset_0_2px_5px_rgba(10,10,10,0.08)] outline-none transition-[border-color,box-shadow] placeholder:text-screwfix-grey-3 hover:border-screwfix-grey-3 focus:border-ring focus:shadow-[0_0_0_3px_var(--screwfix-blue-tint-2),inset_0_2px_5px_rgba(10,10,10,0.06)] disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                disabled={isSubmitting}
                id={inputId}
                inputMode={isPhone ? 'tel' : 'email'}
                onChange={(event) => {
                  setValue(event.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder={isPhone ? phonePlaceholder : emailPlaceholder}
                required
                type={isPhone ? 'tel' : 'email'}
                value={value}
              />

              <div className="min-h-[22px] pt-[5px]">
                {errorMessage ? (
                  <p
                    className="[font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal leading-[17px] text-danger"
                    id={errorId}
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col-reverse gap-[10px] sm:flex-row sm:items-center">
                {!hideCancelAction && cancelText ? (
                  <AlertDialogPrimitive.Cancel asChild>
                    <button
                      className="h-[48px] rounded-[8px] border border-border bg-background px-[20px] [font-family:archivo_narrowbold,sans-serif] text-[clamp(17px,calc(15px_+_0.3vw),18px)] font-bold text-screwfix-blue-shade-3 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50 sm:min-w-[110px]"
                      disabled={isSubmitting}
                      onClick={onCancelAction}
                      type="button"
                    >
                      {cancelText}
                    </button>
                  </AlertDialogPrimitive.Cancel>
                ) : null}

                <button
                  className="button-screwfix-success flex h-[48px] min-h-32 flex-1 items-center justify-center gap-[9px] px-[20px] [font-family:archivo_narrowbold,sans-serif] text-[clamp(18px,calc(16px_+_0.3vw),19px)] font-bold text-primary-foreground disabled:pointer-events-none disabled:opacity-60"
                  disabled={isSubmitting}
                  type="submit"
                >
                  {isSubmitting ? <LoaderCircle aria-hidden="true" className="h-[18px] w-[18px] animate-spin" /> : null}
                  {isSubmitting ? 'Verifying…' : confirmText}
                </button>
              </div>

              <p
                className="mt-[15px] text-center [font-family:archivo_narrowregular,sans-serif] text-[12px] sm:text[14px] font-normal leading-[16px] text-muted-foreground"
                id={helperId}
              >
                {resolvedHelperText}
              </p>
            </form>
          </div>
          <WhatsAppSupport inVerification />
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
